import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const formSubmitAddr = 'https://readdy.ai/api/form/d97p37q4irb0s7p1uvgg';

export default function Footer() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMsg, setSubmitMsg] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('phone_alt') as string || '').trim();
    if (honeypotVal) {
      setSubmitStatus('success');
      setSubmitMsg('Thank you for subscribing!');
      setEmail('');
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch(formSubmitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || responseText;
      if (response.ok && (parsed?.code === 'OK')) {
        setSubmitStatus('success');
        setSubmitMsg('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitStatus('error');
        setSubmitMsg(typeof serverMsg === 'string' ? serverMsg : 'Something went wrong. Please try again.');
      }
    } catch {
      setSubmitStatus('error');
      setSubmitMsg('Network error. Please try again.');
    }
  };

  return (
    <footer className="mx-5 mb-5 rounded-2xl bg-foreground-900 overflow-hidden">
      <div className="px-8 md:px-14 py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <h3 className="font-heading text-3xl md:text-4xl font-extrabold text-background-50 mb-3">
              Hipobuy Sheet
            </h3>
            <p className="text-foreground-300 text-sm leading-relaxed max-w-xs">
              Your go-to resource for curated product spreadsheet finds.
            </p>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-semibold text-background-50 text-sm uppercase tracking-wider mb-5">Navigate</h4>
            <div className="flex flex-col gap-3">
              <a
                href="/"
                onClick={(e) => { e.preventDefault(); navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="text-foreground-300 hover:text-background-50 text-sm transition-colors duration-200 cursor-pointer"
              >
                Home
              </a>
              <a
                href="/hipobuy-spreadsheet-guide"
                onClick={(e) => { e.preventDefault(); navigate('/hipobuy-spreadsheet-guide'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="text-foreground-300 hover:text-background-50 text-sm transition-colors duration-200 cursor-pointer"
              >
                Buying Guides
              </a>
              <a
                href="#categories"
                className="text-foreground-300 hover:text-background-50 text-sm transition-colors duration-200 cursor-pointer"
              >
                Category Index
              </a>
              <a
                href="#articles"
                className="text-foreground-300 hover:text-background-50 text-sm transition-colors duration-200 cursor-pointer"
              >
                Articles
              </a>
              <a
                href="/hipobuy-vs-kakobuy"
                onClick={(e) => { e.preventDefault(); navigate('/hipobuy-vs-kakobuy'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="text-foreground-300 hover:text-background-50 text-sm transition-colors duration-200 cursor-pointer"
              >
                Comparisons
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-foreground-300 hover:text-background-50 text-sm transition-colors duration-200 cursor-pointer"
              >
                Shop Now
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-semibold text-background-50 text-sm uppercase tracking-wider mb-5">Social</h4>
            <div className="flex flex-col gap-3">
              <a href="#" className="flex items-center gap-2.5 text-foreground-300 hover:text-background-50 text-sm transition-colors duration-200 cursor-pointer">
                <i className="ri-instagram-line text-base w-5 h-5 flex items-center justify-center"></i>
                Instagram
              </a>
              <a href="#" className="flex items-center gap-2.5 text-foreground-300 hover:text-background-50 text-sm transition-colors duration-200 cursor-pointer">
                <i className="ri-youtube-line text-base w-5 h-5 flex items-center justify-center"></i>
                YouTube
              </a>
              <a href="#" className="flex items-center gap-2.5 text-foreground-300 hover:text-background-50 text-sm transition-colors duration-200 cursor-pointer">
                <i className="ri-facebook-line text-base w-5 h-5 flex items-center justify-center"></i>
                Facebook
              </a>
            </div>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-semibold text-background-50 text-sm uppercase tracking-wider mb-1">Stay in the loop</h4>
            <p className="text-foreground-300 text-sm mb-5">Get fresh spreadsheets weekly</p>
            <form
              data-readdy-form
              onSubmit={handleNewsletterSubmit}
              className="flex gap-0"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
                className="flex-1 h-12 px-4 bg-foreground-800 text-background-50 text-sm placeholder:text-foreground-400 rounded-l-full border border-foreground-700 focus:outline-none focus:border-accent-400 transition-colors duration-200"
              />
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honeypot"
              />
              <button
                type="submit"
                className="w-12 h-12 flex items-center justify-center bg-accent-500 hover:bg-accent-400 text-background-50 rounded-r-full transition-colors duration-200 flex-shrink-0 cursor-pointer"
              >
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </form>
            {submitStatus !== 'idle' && (
              <p className={`mt-3 text-xs ${submitStatus === 'success' ? 'text-accent-400' : 'text-red-400'}`}>
                {submitMsg}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="relative px-8 md:px-14 pb-10 overflow-hidden select-none pointer-events-none">
        <div className="font-heading text-[6rem] md:text-[10rem] font-black text-foreground-800 leading-none tracking-tighter opacity-20">
          HIPOBUY
        </div>
      </div>

      <div className="border-t border-foreground-800 px-8 md:px-14 py-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-foreground-400 text-xs">
            &copy; {new Date().getFullYear()} Hipobuy Spreadsheet Guide. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <a href="#" className="text-foreground-400 hover:text-background-50 text-xs transition-colors duration-200 cursor-pointer">Privacy Policy</a>
            <a href="#" className="text-foreground-400 hover:text-background-50 text-xs transition-colors duration-200 cursor-pointer">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}